import QRCode from "qrcode.react";
import { ComponentProps, Dispatch, FC, SetStateAction, useState } from "react";
import { confirmPaymentToCashPaid, getInvoiceInfo } from "@payping/redux/actions";
import {
  Badge,
  Button,
  Col,
  Descriptions,
  Drawer,
  Flex,
  Modal,
  Row,
  Skeleton,
  Space,
  Table,
  Tooltip,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import type { PayPingColorType } from "@payping/web-components/utils/types";
import type { ColumnsType } from "@payping/web-components/components/table/types";
import { CuToman, InfoCircleOutlined } from "@payping/icons";
import { invoiceStatus } from "@payping/api";
import type { InvoiceComplexDetailViewModel, InvoiceProductDetailViewModel } from "@payping/api";
import { addOverflow } from "@payping/utils/string";
import { buildUrl } from "@payping/utilities-shared/url";

import { useAsyncRetry } from "react-use";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppTranslation } from "@/utils";
import DetailDate from "@/pages/Shop/Invoice/Detail/Date";
import DetailOperations from "@/pages/Shop/Invoice/Detail/Operations";
import DetailTimeline from "@/pages/Shop/Invoice/Detail/Timeline";
import ScheduleInvoice from "@/pages/Shop/Invoice/Detail/ScheduleDetails/ScheduleInvoice";
import ScheduleInvoiceDates from "@/pages/Shop/Invoice/Detail/ScheduleDetails/ScheduleInvoiceDates";
import ManualPayNoteModal from "./ManualPayNoteModal";
import { LocationStateSearch } from "./types";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { URLS } from "@payping/utilities-shared/consts";

interface Props {
  retryList: () => void;
  code: string;
  setCode: Dispatch<SetStateAction<string | undefined>>;
  openDrawerDetail: boolean;
  setOpenDrawerDetail: Dispatch<SetStateAction<boolean>>;
}

export const InvoiceDetail: FC<Props> = (props) => {
  // =========================================
  // Init
  // =========================================
  const { code, setCode, openDrawerDetail, setOpenDrawerDetail, retryList } = props;
  const { t } = useAppTranslation();
  const location = useLocation();
  const locationSearch = (location.state as LocationStateSearch)?.locationSearch;
  const [invoice, setInvoice] = useState<InvoiceComplexDetailViewModel>();
  const dispatch = useAppDispatch();
  const { loading, retry } = useAsyncRetry(async () => {
    const inv = await dispatch(getInvoiceInfo({ code }));
    setInvoice(inv);
    return inv;
  }, [code, dispatch]);
  const [manualPaymentNotesModalVisible, setManualPaymentNotesModalVisible] = useState(false);
  const data = invoice?.products ?? [];
  const [modal, contextHolder] = Modal.useModal();
  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;

  // =========================================
  // Handlers
  // =========================================
  const [, handleManualPay] = useMessagedAsyncFn(
    async (notes: string) => dispatch(confirmPaymentToCashPaid(code, notes)),
    [code, dispatch],
  );

  // =========================================
  // Subcomponents
  // =========================================
  const btns = (
    <Space>
      <DetailOperations
        key="1"
        code={code}
        retry={retry}
        retryList={retryList}
        data={invoice}
        setManualPaymentNotesModalVisible={setManualPaymentNotesModalVisible}
        loading={loading}
        locationSearch={locationSearch}
        modal={modal}
      />
    </Space>
  );
  const columns: ColumnsType<InvoiceProductDetailViewModel> = [
    {
      dataIndex: "title",
      key: "title",
      render: (value?: string) => addOverflow(value || "-", 30),
      title: t("invoice.detail.item"),
    },
    {
      align: "left",
      dataIndex: "quantity",
      key: "quantity",
      render: (value) => formatLocalizedNumber({ value, options: { maximumFractionDigits: 2 } }),
      title: t("invoice.detail.quantity"),
    },
    {
      align: "left",
      dataIndex: "amount",
      key: "amount",
      render: (value) => formatLocalizedNumber({ value, options: { maximumFractionDigits: 2 } }),
      title: t("invoice.detail.sum"),
    },
    {
      align: "left",
      dataIndex: "taxtionAmount",
      key: "taxtionAmount",
      render: (value) => formatLocalizedNumber({ value, options: { maximumFractionDigits: 2 } }),
      title: t("invoice.detail.taxtionAmount"),
    },
    {
      align: "left",
      dataIndex: "discountAmount",
      key: "discountAmount",
      render: (value) => formatLocalizedNumber({ value, options: { maximumFractionDigits: 2 } }),
      title: t("invoice.detail.discountAmount"),
    },
    {
      align: "left",
      dataIndex: "price",
      key: "price",
      render: (value) => formatLocalizedNumber({ value, options: { maximumFractionDigits: 2 } }),
      title: (
        <Space>
          <Tooltip overlay={t("invoice.detail.priceTooltip")}>
            <InfoCircleOutlined width={"14px"} />
          </Tooltip>
          {t("invoice.detail.price")}
        </Space>
      ),
    },
  ];

  const invoiceURL = invoice?.code ? buildUrl(URLS.PPNG_URL, "v", invoice.code).toString() : "";

  function getColor(color: PayPingColorType | undefined): ComponentProps<typeof Badge>["status"] {
    switch (color) {
      case "cta":
        return "processing";
      case "success":
        return "success";
      case "danger":
        return "error";
      case "delayed":
      case "warning":
        return "warning";
      case "black":
      case "gray500":
      case "gray700":
      default:
        return "default";
    }
  }

  // =========================================
  // Render
  // =========================================
  return (
    <Drawer
      extra={btns}
      size={"large"}
      title={t("invoice.detail.invoicePreview")}
      placement={md ? "left" : "bottom"}
      open={openDrawerDetail}
      onClose={() => {
        setOpenDrawerDetail(false);
        setCode(undefined);
      }}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={invoice?.title}>
              <Descriptions.Item label={t("common.status")}>
                <Badge
                  // @ts-expect-error: status is not undefined TODO: Move getColor to api package
                  status={getColor(invoiceStatus[invoice?.status]?.color)}
                  text={typeof invoice?.status === "number" && invoiceStatus[invoice?.status].name}
                />
              </Descriptions.Item>
              <Descriptions.Item label={"QRCode"}>
                <div className="tw:flex tw:h-18 tw:w-18 tw:flex-col tw:items-center tw:justify-between">
                  <QRCode size={72} className={"detail-image-qr"} value={invoiceURL} />
                </div>
              </Descriptions.Item>
              <Descriptions.Item label={t("common.paymentLink")}>
                <Typography.Link href={invoiceURL} target={"_blank"} rel={"noreferrer"}>
                  {invoiceURL.replace("https://", "")}
                </Typography.Link>
              </Descriptions.Item>
              {invoice?.parentCode ? (
                <Descriptions.Item label={t("invoice.detail.parentCode")}>{invoice?.parentCode}</Descriptions.Item>
              ) : null}
              <Descriptions.Item label={t("invoice.detail.invoiceDate")}>
                {toJalali({ date: invoice?.sendDate, toFormat: dateFormats.ui.jalaliDate }).string}
              </Descriptions.Item>
              <Descriptions.Item label={t("invoice.detail.invoiceNumber")}>{invoice?.number}</Descriptions.Item>
              <Descriptions.Item label={t("archive")}>
                {invoice?.archivedDateTime ? t("archived") : t("notArchived")}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        <Col xs={24} lg={12}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={t("invoice.detail.customerDetail")}>
              {invoice?.customer?.fullName ? (
                <Descriptions.Item label={t("firstName")}>{invoice?.customer?.fullName}</Descriptions.Item>
              ) : null}
              {invoice?.customer?.state || invoice?.customer?.city ? (
                <Descriptions.Item label={t("city")}>
                  {invoice?.customer?.state} {invoice?.customer?.city}
                </Descriptions.Item>
              ) : null}
              {invoice?.customer?.location ? (
                <Descriptions.Item label={t("address")}>{invoice?.customer?.location}</Descriptions.Item>
              ) : null}
              {invoice?.customer?.zipCode ? (
                <Descriptions.Item label={t("postalCode")}>{invoice?.customer?.zipCode}</Descriptions.Item>
              ) : null}
              {invoice?.customer?.phone ? (
                <Descriptions.Item label={t("phone")}>{invoice?.customer?.phone}</Descriptions.Item>
              ) : null}
              {invoice?.customer?.email ? (
                <Descriptions.Item label={t("email")}>{invoice?.customer?.email}</Descriptions.Item>
              ) : null}
            </Descriptions>
          )}
        </Col>
        <Col xs={24} lg={12}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={t("common.financial")}>
              <Descriptions.Item
                label={
                  <span>
                    {t("invoice.detail.amount")}{" "}
                    <Tooltip overlay={t("invoice.detail.amountTooltip")}>
                      <InfoCircleOutlined width={"14px"} />
                    </Tooltip>
                  </span>
                }
              >
                <Flex align="center">
                  <Typography.Text type="secondary">
                    {formatLocalizedNumber({ value: invoice?.subTotal ?? 0, options: { maximumFractionDigits: 2 } })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Descriptions.Item>
              <Descriptions.Item label={t("invoice.detail.transportation")}>
                <Flex align="center">
                  <Typography.Text type="secondary">
                    {formatLocalizedNumber({ value: invoice?.shipping ?? 0, options: { maximumFractionDigits: 2 } })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Descriptions.Item>
              <Descriptions.Item label={t("invoice.detail.totalTax")}>
                <Flex align="center">
                  <Typography.Text type="secondary">
                    {formatLocalizedNumber({
                      value: invoice?.taxtionAmount ?? 0,
                      options: { maximumFractionDigits: 2 },
                    })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Descriptions.Item>
              <Descriptions.Item label={t("invoice.detail.totalDiscount")}>
                <Flex align="center">
                  <Typography.Text type="secondary">
                    {formatLocalizedNumber({
                      value: invoice?.totalDiscountAmount ?? 0,
                      options: { maximumFractionDigits: 2 },
                    })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Descriptions.Item>
              <Descriptions.Item label={t("invoice.detail.total")}>
                <Flex align="center">
                  <Typography.Text type="secondary">
                    {formatLocalizedNumber({ value: invoice?.total ?? 0, options: { maximumFractionDigits: 2 } })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        {invoice?.notes || invoice?.termsAndConditions || invoice?.memo || invoice?.attachmentUrls?.length ? (
          <Col xs={24}>
            {loading ? (
              <Skeleton active paragraph />
            ) : (
              <Descriptions className="tw:w-full" column={1} bordered title={t("common.other")}>
                <Descriptions.Item label={t("invoice.detail.message")}>{invoice?.notes}</Descriptions.Item>
                <Descriptions.Item label={t("invoice.detail.rules")}>{invoice?.termsAndConditions}</Descriptions.Item>
                <Descriptions.Item label={t("invoice.detail.memo")}>{invoice?.memo}</Descriptions.Item>
                <Descriptions.Item label={t("invoice.detail.files")}>
                  {invoice?.attachmentUrls?.map((url, i) => (
                    <Col key={i}>
                      <a href={url} target={"_blank"} rel={"noreferrer"} download={`Invoice file ${i}`}>
                        <Button key={i} type="primary">
                          {t("invoice.detail.downloadFile")} {i + 1}
                        </Button>
                      </a>
                    </Col>
                  ))}
                </Descriptions.Item>
              </Descriptions>
            )}
          </Col>
        ) : null}
        <Col span={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Table<InvoiceProductDetailViewModel> dataSource={data} columns={columns} />
          )}
        </Col>
        <Col span={24}>
          <DetailDate
            dueDate={invoice?.dueDate}
            sendDate={invoice?.sendDate}
            payedDateTime={invoice?.payedDateTime}
            status={invoice?.status}
          />
        </Col>
        <Col span={24}>
          <DetailTimeline code={code} />
        </Col>
        {invoice?.schedule && (
          <Col span={24}>
            <ScheduleInvoice
              dueDateAfterHowManyDay={invoice.schedule.dueDateAfterHowManyDay}
              interval={invoice.schedule.interval}
              scheduleType={invoice.schedule.scheduleType}
              startDate={invoice.sendDate}
              count={invoice.schedule.count}
              sentCount={invoice.invoicesWithSameParent.length}
            />
          </Col>
        )}
        {invoice?.invoicesWithSameParent && invoice.invoicesWithSameParent.length > 0 && (
          <Col span={24}>
            <ScheduleInvoiceDates count={invoice.schedule?.count} data={invoice.invoicesWithSameParent} />
          </Col>
        )}
      </Row>
      {contextHolder}
      <ManualPayNoteModal
        visible={manualPaymentNotesModalVisible}
        onCancel={() => setManualPaymentNotesModalVisible(false)}
        onOk={async (notes: string) => {
          setManualPaymentNotesModalVisible(false);
          await handleManualPay(notes);
          retryList();
        }}
      />
    </Drawer>
  );
};

export default InvoiceDetail;
